// Source → dimension contribution map.

import { type Dimension } from './dimensions';

export const SOURCES = [
  'accounting',
  'crm',
  'hr',
  'product_eng',
  'strategy_interview',
  'spot_survey',
  'leadership_log',
  'support',
  'marketing',
  'calendar',
] as const;

export type SourceSystem = (typeof SOURCES)[number];

export interface SourceMeta {
  id: SourceSystem;
  label: string;
  weight_pct: number; // overall share of composite
  cadence: 'monthly' | 'weekly' | 'sprint' | 'quarterly' | 'random_17d';
  contributes_to: Dimension[];
  examples: string[]; // vendor examples
}

export const SOURCE_META: Record<SourceSystem, SourceMeta> = {
  accounting: {
    id: 'accounting',
    label: 'Accounting / Finance',
    weight_pct: 22,
    cadence: 'monthly',
    contributes_to: ['financial_vitality', 'risk'],
    examples: ['QuickBooks', 'NetSuite', 'Xero', 'Zoho Books', 'FreshBooks'],
  },
  crm: {
    id: 'crm',
    label: 'CRM / Revenue',
    weight_pct: 20,
    cadence: 'monthly',
    contributes_to: ['gtm', 'product_customer', 'financial_vitality'],
    examples: ['HubSpot', 'Salesforce', 'Pipedrive', 'Attio', 'Close'],
  },
  hr: {
    id: 'hr',
    label: 'HR / People Ops',
    weight_pct: 14,
    cadence: 'quarterly',
    contributes_to: ['culture_people', 'c_suite'],
    examples: ['Rippling', 'BambooHR', 'Lattice', 'Gusto', 'Justworks'],
  },
  product_eng: {
    id: 'product_eng',
    label: 'Product / Engineering',
    weight_pct: 11,
    cadence: 'sprint',
    contributes_to: ['delivery_execution', 'product_customer'],
    examples: ['GitHub', 'Jira', 'Linear', 'Shortcut'],
  },
  strategy_interview: {
    id: 'strategy_interview',
    label: 'Strategy / Market Intel',
    weight_pct: 11,
    cadence: 'quarterly',
    contributes_to: ['vision_strategy', 'market_competition'],
    examples: ['Zoe-guided 12-question interview', 'LexisNexis'],
  },
  spot_survey: {
    id: 'spot_survey',
    label: 'Spot Surveys (AI)',
    weight_pct: 6,
    cadence: 'random_17d',
    contributes_to: ['culture_people', 'c_suite'],
    examples: ['Zoe-native AI dispatch'],
  },
  leadership_log: {
    id: 'leadership_log',
    label: 'Leadership Tracking',
    weight_pct: 4,
    cadence: 'quarterly',
    contributes_to: ['c_suite'],
    examples: ['Zoe-native decision log + delegation review'],
  },
  support: {
    id: 'support',
    label: 'Support Platform',
    weight_pct: 1.5,
    cadence: 'weekly',
    contributes_to: ['product_customer'],
    examples: ['Zendesk', 'Intercom', 'Help Scout'],
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing Platform',
    weight_pct: 1.5,
    cadence: 'monthly',
    contributes_to: ['gtm'],
    examples: ['HubSpot Marketing', 'GA4', 'Mixpanel'],
  },
  calendar: {
    id: 'calendar',
    label: 'Calendar Audit',
    weight_pct: 0.5,
    cadence: 'weekly',
    contributes_to: ['c_suite'],
    examples: ['Google Calendar', 'Outlook'],
  },
};

const sourceSum = SOURCES.reduce((acc, s) => acc + SOURCE_META[s].weight_pct, 0);
if (sourceSum < 85 || sourceSum > 100) {
  throw new Error(`Source system weights should sum 85–100; got ${sourceSum}`);
}

// Reverse-map: dimension → list of sources that contribute to it.
export function sourcesFor(dimension: Dimension): SourceSystem[] {
  return SOURCES.filter((s) => SOURCE_META[s].contributes_to.includes(dimension));
}
